import { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { ConfirmationModal } from '../components';
import { parse, stringify } from './urlHelpers';
import { PROVENANCE_VALUES } from '../components/ProvenanceFilter/ProvenanceFilter';

const SHAKE_ANIMATION_DELAY = 4000;

export const useShakeAnimation = (containerRef, delay = SHAKE_ANIMATION_DELAY) => {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const container = containerRef.current;

    function restartAnimation() {
      if (isMounted) {
        setIsAnimating(true);
        void container.offsetWidth;
      }
    }

    function handleAnimationEnd() {
      if (isMounted) {
        setIsAnimating(false);
        setTimeout(restartAnimation, delay);
      }
    }

    container.addEventListener('animationend', handleAnimationEnd);
    restartAnimation();

    return () => {
      isMounted = false;
      container.removeEventListener('animationend', handleAnimationEnd);
    };
  }, [containerRef, delay]);

  return { isAnimating };
};

/**
 * Wait for an element before resolving a promise
 * @param {String} querySelector - Selector of element to wait for
 * @param {Integer} timeout - Milliseconds to wait before timing out, or 0 for no timeout
 */
export const waitForElement = (querySelector, timeout) => {
  return new Promise((resolve, reject) => {
    var timer = false;
    if (document.querySelectorAll(querySelector).length) return resolve();
    const observer = new MutationObserver(() => {
      if (document.querySelectorAll(querySelector).length) {
        observer.disconnect();
        if (timer !== false) clearTimeout(timer);
        return resolve();
      }
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
    if (timeout)
      timer = setTimeout(() => {
        observer.disconnect();
        reject();
      }, timeout);
  });
};

/**
 * Custom hook that tracks the width of a DOM element using the ResizeObserver API.
 *
 * @returns {Object} An object containing:
 * - elementRef {React.RefObject}: A ref to be attached to the target DOM element.
 * - elementWidth {number}: The current width of the element.
 */
export const useElementSize = () => {
  const elementRef = useRef(null);
  const [elementWidth, setElementWidth] = useState(0);

  useEffect(() => {
    let isMounted = true;

    const resizeObserver = new ResizeObserver(entries => {
      if (!isMounted) return;
      for (let entry of entries) {
        setElementWidth(entry.contentRect.width);
      }
    });

    if (elementRef.current) {
      resizeObserver.observe(elementRef.current);
    }

    return () => {
      isMounted = false;
      if (elementRef.current) {
        resizeObserver.unobserve(elementRef.current);
      }
    };
  }, []);

  return { elementRef, elementWidth };
};

/**
 * Custom hook that simulates a loading state for a specified delay.
 *
 * @param {number} [delay=1000] - The delay in milliseconds before loading is set to false (default is 1000ms).
 *
 * @returns {boolean} A boolean indicating whether the element is still in a loading state (true if loading, false otherwise).
 */
export const useElementLoaded = (delay = 1000) => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, [delay]);
  }, []);

  return loading;
};

/**
 * A simple debounce function to limit how often a function can run.
 *
 * @param {Function} func - The function to debounce.
 * @param {number} delay - The delay in milliseconds.
 * @returns {Function} - The debounced function.
 */
const debounce = (func, delay) => {
  let timeout;
  return function(...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), delay);
  };
};

/**
 * A custom hook that prevents horizontal scrolling on a given element
 * only if the device is not mobile. It also updates based on viewport resizing.
 *
 * @param {React.Ref} ref - A React ref that points to the element to prevent horizontal scrolling on.
 * @param {number} maxWidth - The maximum width for non-mobile devices, defaults to 768.
 */
export const usePreventHorizontalScroll = (ref, maxWidth = 768) => {
  useEffect(() => {
    const element = ref.current;

    const preventHorizontalScroll = event => {
      if (event.deltaX !== 0) {
        event.preventDefault();
      }
    };

    const updateScrollListeners = () => {
      const isMobile = window.innerWidth < maxWidth;

      if (!isMobile && element) {
        element.addEventListener('wheel', preventHorizontalScroll, { passive: false });
        element.addEventListener('touchmove', preventHorizontalScroll, { passive: false });
      } else if (element) {
        element.removeEventListener('wheel', preventHorizontalScroll);
        element.removeEventListener('touchmove', preventHorizontalScroll);
      }
    };

    updateScrollListeners();

    const debouncedResizeListener = debounce(updateScrollListeners, 100);

    window.addEventListener('resize', debouncedResizeListener);

    return () => {
      window.removeEventListener('resize', debouncedResizeListener);
      if (element) {
        element.removeEventListener('wheel', preventHorizontalScroll);
        element.removeEventListener('touchmove', preventHorizontalScroll);
      }
    };
  }, [ref, maxWidth]);
};

/**
 * Custom hook for managing confirmation modals with two usage patterns:
 *
 * 1. Simple Promise-based pattern (original):
 *    - Use `open()` which returns a Promise
 *    - Modal closes automatically after user action
 *    - Good for simple confirmations
 *
 * 2. Custom handlers pattern (new):
 *    - Use `openWithHandlers()` with custom onConfirm/onCancel functions
 *    - Full control over modal state and closing
 *    - Good for complex operations with loading states
 *
 * @returns {Object} Hook interface
 * @returns {Function} open - Opens modal and returns Promise<boolean>
 * @returns {Function} openWithHandlers - Opens modal with custom handlers
 * @returns {Function} updateProps - Updates modal properties (e.g., inProgress state)
 * @returns {Function} closeModal - Manually closes the modal
 * @returns {JSX.Element|null} modalElement - Modal component to render
 */
export const useConfirmationModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [resolve, setResolve] = useState(null);
  const [modalProps, setModalProps] = useState({
    id: null,
    title: null,
    description: null,
    confirmMessage: null,
    cancelMessage: null,
    inProgress: false,
    onConfirm: null,
    onCancel: null,
  });

  const modalId = modalProps.id || 'ConfirmationModal';

  const open = useCallback(
    ({ id, title, description, confirmMessage, cancelMessage, inProgress } = {}) => {
      setModalProps({
        id,
        title,
        description,
        confirmMessage,
        cancelMessage,
        inProgress,
        onConfirm: null,
        onCancel: null,
      });
      return new Promise(res => {
        setResolve(() => res);
        setIsOpen(true);
      });
    },
    []
  );

  const openWithHandlers = useCallback(
    ({
      id,
      title,
      description,
      confirmMessage,
      cancelMessage,
      inProgress,
      onConfirm,
      onCancel,
    } = {}) => {
      setModalProps({
        id,
        title,
        description,
        confirmMessage,
        cancelMessage,
        inProgress: inProgress || false,
        onConfirm,
        onCancel,
      });
      setResolve(null);
      setIsOpen(true);
    },
    []
  );

  const updateProps = useCallback(newProps => {
    setModalProps(prev => ({ ...prev, ...newProps }));
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleConfirm = () => {
    if (modalProps.onConfirm) {
      modalProps.onConfirm();
    } else {
      if (resolve) resolve(true);
      setIsOpen(false);
    }
  };

  const handleCancel = () => {
    if (modalProps.onCancel) {
      modalProps.onCancel();
    } else {
      if (resolve) resolve(false);
      setIsOpen(false);
    }
  };

  const modalElement = isOpen && (
    <ConfirmationModal
      id={modalId}
      isOpen={isOpen}
      inProgress={modalProps.inProgress}
      title={modalProps.title}
      description={modalProps.description}
      confirmMessage={modalProps.confirmMessage}
      cancelMessage={modalProps.cancelMessage}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      onCloseModal={() => setIsOpen(false)}
    />
  );

  return {
    open,
    openWithHandlers,
    updateProps,
    closeModal,
    modalElement,
  };
};

/**
 * Custom hook to manage the removal and restoration of the "Getting Started" page using local storage.
 *
 * @param {string} userId - The ID of the current user.
 * @param {string} [orgId] - Optional organization ID for per-org tracking.
 * @returns {Object} An object containing:
 *   - `isRemoved` {boolean} - Whether the page has been removed for the current user/org based on local storage.
 *   - `removePage` {function} - A function to mark the page as removed in local storage.
 *   - `restorePage` {function} - A function to restore the page by removing the flag from local storage.
 */
export const useGettingStartedRemoval = (userId, orgId) => {
  const [isRemoved, setIsRemoved] = useState(false);

  const entryKey = orgId ? `${userId}_${orgId}` : userId;

  useEffect(() => {
    const storedData = localStorage.getItem('gettingStartedRemoved');
    const gettingStartedRemoved = storedData ? JSON.parse(storedData) : [];

    const entry = gettingStartedRemoved.find(e => e.id === entryKey);
    if (entry) {
      setIsRemoved(entry.isRemoved);
    } else {
      setIsRemoved(false);
    }
  }, [entryKey]);

  const removePage = () => {
    const storedData = localStorage.getItem('gettingStartedRemoved');
    const gettingStartedRemoved = storedData ? JSON.parse(storedData) : [];

    const entry = gettingStartedRemoved.find(e => e.id === entryKey);
    if (entry) {
      entry.isRemoved = true;
    } else {
      gettingStartedRemoved.push({ id: entryKey, isRemoved: true });
    }

    localStorage.setItem('gettingStartedRemoved', JSON.stringify(gettingStartedRemoved));
    setIsRemoved(true);
  };

  const restorePage = () => {
    const storedData = localStorage.getItem('gettingStartedRemoved');
    const gettingStartedRemoved = storedData ? JSON.parse(storedData) : [];

    const entry = gettingStartedRemoved.find(e => e.id === entryKey);
    if (entry) {
      entry.isRemoved = false;
      localStorage.setItem('gettingStartedRemoved', JSON.stringify(gettingStartedRemoved));
      setIsRemoved(false);
    }
  };

  return {
    isRemoved,
    removePage,
    restorePage,
  };
};

/**
 * Custom hook to check if the user's IP address is from Canada.
 *
 * @returns {boolean | null} - Returns `true` if the user is from Canada, `false` otherwise.
 * Returns `null` while the location is being fetched.
 */
export const useIsFromCanada = () => {
  const [isFromCanada, setIsFromCanada] = useState(null);

  useEffect(() => {
    /**
     * Fetches the user's country based on their IP address.
     * Uses the `ipapi.co` API to retrieve geolocation data.
     */
    const fetchCountry = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        setIsFromCanada(data.country_code === 'CA');
      } catch (error) {
        console.error('Error fetching country data:', error);
        setIsFromCanada(false);
      }
    };

    fetchCountry();
  }, []);

  return isFromCanada;
};

// ─────────────────────────────────────────────────────────────────────────
// Dashboard-side hooks
// ─────────────────────────────────────────────────────────────────────────
//
// The dashboard convention is: page bodies don't host raw useEffect calls.
// Mount-time data fetches go through the page duck's loadData (see
// pageDataLoadingAPI.js); everything else lives behind a named hook so
// the page reads as a list of intents instead of a list of effects.

/**
 * Run `callback(value)` exactly when `value` changes (including the
 * initial render). The callback's return value is honored as the
 * useEffect cleanup, so subscriptions can be torn down on the next
 * run / unmount. Thin wrapper over useEffect — page components consume
 * the named hook, never the raw effect.
 */
export const useReactToChange = (value, callback) => {
  useEffect(() => callback(value), [value]);
};

/**
 * Sync an outer value into a ref. Used by components that need a stable
 * reference inside long-lived handlers (e.g. Sendbird real-time
 * callbacks that close over the prop at attach time).
 */
export const useSyncedRef = value => {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref;
};

/**
 * Track whether the viewport is narrower than `maxWidth` (default 768).
 * Encapsulates the resize listener so components (e.g. the responsive Table)
 * stay free of raw useEffects. SSR-safe: reports false until mounted, and the
 * listener is only attached while `enabled`.
 */
export const useIsNarrowViewport = (maxWidth = 768, enabled = true) => {
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return undefined;
    }
    const check = () => setIsNarrow(window.innerWidth < maxWidth);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [maxWidth, enabled]);
  return isNarrow;
};

/**
 * Fire confetti exactly once when `allTasksCompleted` first becomes true,
 * gated by a localStorage key so the celebration doesn't repeat across
 * sessions. `fire` is the project-specific confetti routine — kept as a
 * caller-provided fn so this hook stays free of the canvas-confetti dep.
 */
export const useConfettiOnce = ({ allTasksCompleted, storageKey, fire }) => {
  const firedRef = useRef(false);
  useEffect(() => {
    if (!allTasksCompleted || firedRef.current) return;
    if (storageKey && typeof window !== 'undefined' && localStorage.getItem(storageKey)) return;
    firedRef.current = true;
    if (storageKey && typeof window !== 'undefined') localStorage.setItem(storageKey, 'true');
    fire();
  }, [allTasksCompleted]);
};

/**
 * Open the onboarding wizard once when the duck signals it should be
 * auto-shown. The hook is gated by `enabled` so the page can suppress
 * auto-open for non-owners/admins without an extra branch.
 *
 *   open()           — page-provided opener (sets local UI state)
 *   markShown()      — page-provided redux action to clear shouldAutoShow
 */
export const useWizardAutoOpen = ({ wizardState, enabled, open, markShown }) => {
  useEffect(() => {
    if (!enabled) return;
    if (wizardState?.dataLoaded && wizardState?.shouldAutoShow) {
      open(wizardState.startingStep);
      markShown();
    }
  }, [wizardState?.dataLoaded, wizardState?.shouldAutoShow, wizardState?.startingStep, enabled]);
};

/**
 * URL-backed provenance filter ('all' | 'owned' | 'rented'). The active
 * value lives in `?source=…` so it survives reloads and copy-paste links.
 *
 *   const [provenance, setProvenance] = useProvenanceQuery(history, location);
 *
 * Implementation notes:
 *   • Reads from `location.search` so the value is always in sync with
 *     the current URL — no useEffect needed.
 *   • Invalid / missing values fall back to 'all' (matches the chip
 *     default and avoids surprising the user with an empty list).
 *   • Setter preserves every other query param via the parse → spread →
 *     stringify dance. `source=all` is dropped from the URL since it's
 *     the default; only 'owned' / 'rented' get written.
 */
export const useProvenanceQuery = (history, location) => {
  const search = location?.search || '';

  const value = useMemo(() => {
    const parsed = parse(search) || {};
    const raw = parsed.source;
    return PROVENANCE_VALUES.includes(raw) ? raw : 'all';
  }, [search]);

  const setValue = useCallback(
    next => {
      if (!history) return;
      const parsed = parse(search) || {};
      const nextParams = { ...parsed };
      if (!next || next === 'all') {
        delete nextParams.source;
      } else {
        nextParams.source = next;
      }
      const nextSearch = stringify(nextParams);
      history.push({
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      });
    },
    [history, location?.pathname, search]
  );

  return [value, setValue];
};
